<template>
  <artisio-modal
      :visible="showModal"
      :title="$t('Bulk Edit')"
      @hidden="onHidden"
      size="l"
      @ok="onSubmit"
      :disable-submit-btn="submitDisabled"
  >
    <div class="row">
      <div class="col-2 pl-0 text-center">
        <label>{{ $t('Clear') }}</label>
        <field-title-hint target="clear-checkbox"
                          :title="$t('If the \'Clear\' checkbox is chosen, the value(s) for the chosen field(s) will be cleared.')">
        </field-title-hint>
        <input-widget
            class="mt-1" :model="emptyModel" attribute="proposal_suggestion" type="checkbox" :label="false" size="lg"
            @change="onEmptyClick($event, 'proposal_suggestion')">
        </input-widget>
      </div>
      <div class="col-10 pl-0">
        <input-widget :model="presetModel" attribute="proposal_suggestion" type="select2"
                      value-field="value" display-field="${item.key}"
                      :items="suggestionOptions" :rules="{required: !!presetModel.proposal_status}"
                      title-hint @select2Select="onInputChange('proposal_suggestion')"/>
      </div>
    </div>

    <div class="row">
      <div class="col-2 pl-0 empty-checkbox">
        <input-widget
            class="mt-1" :model="emptyModel" attribute="proposal_status" type="checkbox" :label="false" size="lg"
            @change="onEmptyClick($event, 'proposal_status')">
        </input-widget>
      </div>
      <div class="col-10 pl-0">
        <input-widget :model="presetModel" attribute="proposal_status" type="select2"
                      value-field="value" display-field="${item.key}"
                      :items="proposalStatusOptions"
                      title-hint @select2Select="onInputChange('proposal_status')"/>
      </div>
    </div>

    <div class="row">
      <div class="col-2 pl-0 empty-checkbox">
        <input-widget
            class="mt-1" :model="emptyModel" attribute="new_reserve" type="checkbox" :label="false" size="lg"
            @change="onEmptyClick($event, 'new_reserve')">
        </input-widget>
      </div>
      <div class="col-10 pl-0">
        <currency-input-widget :model="presetModel" attribute="new_reserve" type="number"
                               :disabled="newValuesDisabled" title-hint
                               :append-question="getAmountFieldQuestionText('new_reserve')"
                               :currency="currencySymbol" @input="onInputChange('new_reserve')"/>
      </div>
    </div>

    <div class="row">
      <div class="col-2 pl-0 empty-checkbox">
        <input-widget
            class="mt-1" :model="emptyModel" attribute="new_low" type="checkbox" :label="false" size="lg"
            @change="onEmptyClick($event, 'new_low')">
        </input-widget>
      </div>
      <div class="col-10 pl-0">
        <currency-input-widget :model="presetModel" attribute="new_low" type="number"
                               :disabled="newValuesDisabled" title-hint
                               :append-question="getAmountFieldQuestionText('new_low')"
                               :currency="currencySymbol" @input="onInputChange('new_low')"/>
      </div>
    </div>

    <div class="row">
      <div class="col-2 pl-0 empty-checkbox">
        <input-widget
            class="mt-1" :model="emptyModel" attribute="new_high" type="checkbox" :label="false" size="lg"
            @change="onEmptyClick($event, 'new_high')">
        </input-widget>
      </div>
      <div class="col-10 pl-0">
        <currency-input-widget :model="presetModel" attribute="new_high" type="number"
                               :disabled="newValuesDisabled" title-hint
                               :append-question="getAmountFieldQuestionText('new_high')"
                               :currency="currencySymbol" @input="onInputChange('new_high')"/>
      </div>
    </div>

    <div class="row">
      <div class="col-2 pl-0 empty-checkbox">
        <input-widget
            class="mt-1" :model="emptyModel" attribute="new_start_price" type="checkbox" :label="false" size="lg"
            @change="onEmptyClick($event, 'new_start_price')">
        </input-widget>
      </div>
      <div class="col-10 pl-0">
        <currency-input-widget :model="presetModel" attribute="new_start_price" type="number"
                               :disabled="newValuesDisabled" title-hint
                               :append-question="getAmountFieldQuestionText('new_start_price')"
                               :currency="currencySymbol" @input="onInputChange('new_start_price')"/>
      </div>
    </div>

    <div class="row">
      <div class="col-2 pl-0 empty-checkbox">
        <input-widget
          class="mt-1" :model="emptyModel" attribute="new_reserve_type" type="checkbox" :label="false" size="lg"
          @change="onEmptyClick($event, 'new_reserve_type')">
        </input-widget>
      </div>
      <div class="col-10 pl-0">
        <input-widget
          :model="presetModel"
          attribute="new_reserve_type"
          type="select2"
          value-field="value"
          display-field="${item.key}"
          :items="reserveTypes"
          :disabled="newValuesDisabled"
          @select2Select="onInputChange('new_reserve_type')"
        />
      </div>
    </div>

  </artisio-modal>
</template>


<script>
import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal.vue";
import {createNamespacedHelpers} from "vuex";
import InputWidget from "@/core/components/input-widget/InputWidget.vue";
import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget.vue";
import {LotProposalEditModel, LotProposalEmptyEditModel} from "@/modules/vendors/LotProposalEdit.model";
import FieldTitleHint from "@/modules/inventory/components/FieldTitleHint.vue";
import {ON_UNSOLD_LOT_PROPOSAL_BULK_INPUT} from "@/plugins/event-bus/event-bus-names";

const {mapState, mapActions} = createNamespacedHelpers('vendors/unsoldProposals');
const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
export default {
  name: "LotProposalBulkEditModal",
  components: {FieldTitleHint, CurrencyInputWidget, InputWidget, ArtisioModal},
  data: () => {
    return {
      presetModel: new LotProposalEditModel(),
      emptyModel: new LotProposalEmptyEditModel()
    }
  },
  computed: {
    ...mapStateGlobal({
      enableInventoryQuantity: state => state.enableInventoryQuantity,
      reserveTypes: state => state.reserveTypes,
    }),
    ...mapState({
      showModal: state => state.lotBulkEditModal.show,
      reservation_uuids: state => state.lotBulkEditModal.reservation_uuids,
      suggestionOptions: state => state.suggestionOptions,
      proposalStatusOptions: state => state.proposalStatusOptions,
      auction: state => state.unsoldProposalModal.auction
    }),
    newValuesDisabled() {
      return this.presetModel.proposal_suggestion !== 'reoffer'
    },
    currencySymbol() {
      if (!this.auction) return ''
      return this.auction.currency.symbol;
    },
    submitDisabled() {
      if (!this.presetModel) return false
      return !!this.presetModel.proposal_status && !this.presetModel.proposal_suggestion;
    },
  },
  methods: {
    ...mapActions(['hideLotBulkEditModal']),
    onHidden() {
      this.presetModel = new LotProposalEditModel()
      this.emptyModel = new LotProposalEmptyEditModel()
      this.hideLotBulkEditModal()
    },
    onSubmit() {
      if (this.presetModel.new_low && this.presetModel.new_high && parseFloat(this.presetModel.new_low) > parseFloat(this.presetModel.new_high)) {
        this.$errorDialog(this.$i18n.t('New Low Estimate value must be less than New High Estimate value.'))
        return
      }
      this.$evBus.$emit(ON_UNSOLD_LOT_PROPOSAL_BULK_INPUT, {
        emptyModel: this.emptyModel,
        presetModel: this.presetModel,
        reservation_uuids: this.reservation_uuids
      })
      this.hideLotBulkEditModal()
    },
    getAmountFieldQuestionText(attribute) {
      if (!this.enableInventoryQuantity) {
        return null;
      }
      let fieldName = this.$options.filters.removeUnderscores(attribute);
      fieldName = this.$options.filters.toUpperCaseFirst(fieldName);
      return this.$i18n.t("{fieldName} amount given for single quantity of the item", {fieldName})
    },
    onEmptyClick(event, field) {
      this.emptyModel[field] = event
      if (event) {
        this.presetModel[field] = null
      }
    },
    onInputChange(field) {
      this.emptyModel[field] = false
      if (field === 'proposal_suggestion') {
        this.presetModel.proposal_status = 'suggestion_not_sent';
      }
    }
  },
  watch: {
    'presetModel.proposal_suggestion'() {
      if (!this.presetModel) return
      if (this.presetModel.proposal_suggestion !== 'reoffer') {

        this.presetModel.new_reserve = null
        this.presetModel.new_low = null
        this.presetModel.new_high = null
        this.presetModel.new_start_price = null
        this.presetModel.new_reserve_type = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.collection-info-card {
  .card-header {
    /deep/ .form-group {
      margin-bottom: 0;
    }
  }
}

.empty-checkbox {
  margin-top: 2rem;
  text-align: center;
}
</style>
